import { format } from "date-fns";
import { Link } from "react-router-dom";

import { formatPercent } from "./chartTheme";
import StatCard, { Empty } from "./StatCard";

const day = (value) => (value ? format(new Date(value), "dd MMM") : "-");

const PersonRow = ({ person, meta, to, tone }) => (
  <li className="ws-person">
    <div className="ws-person__who">
      <Link to={`/employees/${person.id}`} className="table-link">{person.name}</Link>
      <div className="ws-muted">
        {[person.emp_no, person.position, person.cost_center].filter(Boolean).join(" · ")}
      </div>
    </div>
    <div className={`ws-person__meta${tone ? ` is-${tone}` : ""}`}>
      {to ? <Link to={to} className="ws-person__date">{meta}</Link> : <span className="ws-person__date">{meta}</span>}
    </div>
  </li>
);

const PeopleList = ({ people, empty, renderMeta, linkFor, toneFor }) =>
  people?.length ? (
    <ul className="ws-people">
      {people.map((p, index) => (
        <PersonRow key={`${p.id}-${index}`} person={p} meta={renderMeta(p)} to={linkFor?.(p)} tone={toneFor?.(p)} />
      ))}
    </ul>
  ) : (
    <Empty>{empty}</Empty>
  );

/** What HR has to act on soon. Every name opens the employee. */
export const UpcomingSection = ({ upcoming }) => {
  if (!upcoming) return null;
  const days = upcoming.days;
  const overdue = upcoming.pending_resignations.filter((p) => p.overdue).length;
  return (
    <div className="ws-grid ws-grid--3">
      <StatCard title={`Leaving in the next ${days} days`} hint="BC termination date, or a resignation approved in ESS" aside={<span className="ws-count">{upcoming.leaving.length}</span>}>
        <PeopleList people={upcoming.leaving} empty="Nobody is due to leave." renderMeta={(p) => `${day(p.date)} · ${p.reason}`} />
      </StatCard>
      <StatCard title="Pending resignations" hint={overdue ? `Awaiting approval · ${overdue} already past their last working day` : "Awaiting approval, by last working day"} aside={<span className="ws-count">{upcoming.pending_resignations.length}</span>}>
        <PeopleList
          people={upcoming.pending_resignations}
          empty="No resignations waiting."
          renderMeta={(p) => (p.overdue ? `LWD passed · ${day(p.date)}` : `LWD ${day(p.date)}`)}
          linkFor={(p) => `/resignations/${p.resignation_id}`}
          toneFor={(p) => (p.overdue ? "overdue" : null)}
        />
      </StatCard>
      <StatCard title={`Confirmations due in ${days} days`} hint="Hire date + 180 days (the BC probation rule)" aside={<span className="ws-count">{upcoming.confirmations_due.length}</span>}>
        <PeopleList people={upcoming.confirmations_due} empty="No confirmations due." renderMeta={(p) => day(p.date)} />
      </StatCard>
    </div>
  );
};

/** Managers with the most direct reports in this selection. */
export const TopManagers = ({ managers }) => (
  <PeopleList
    people={managers}
    empty="No reporting lines on record."
    renderMeta={(m) => `${m.reports} report${m.reports === 1 ? "" : "s"}`}
  />
);

/** Share of current employees with each field filled in, emptiest first. */
export const DataCompleteness = ({ fields }) =>
  fields?.length ? (
    <ul className="ws-quality">
      {fields.map((f) => (
        <li key={f.field} className="ws-quality__row">
          <span className="ws-quality__label">{f.label}</span>
          <div className="ws-quality__track" aria-hidden="true">
            <div className={`ws-quality__bar ${f.complete_rate < 90 ? "is-low" : ""}`} style={{ width: `${f.complete_rate || 0}%` }} />
          </div>
          <span className="ws-quality__value">
            {formatPercent(f.complete_rate)}
            {f.missing > 0 && <span className="ws-muted"> · {f.missing} missing</span>}
          </span>
        </li>
      ))}
    </ul>
  ) : (
    <Empty />
  );
